import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import {
  LinkableDrug,
  addDoseToDrug,
  createDrug,
  searchDrugsForProductLink,
} from "../../../../api/inventoryService";
import { scaleLabel, strengthUnits } from "../../../../data/scale/scale";
import {
  LinkedDose,
  doseFieldLabel,
  isPerUnitScale,
  linkSummary,
} from "../../productDoseLink";

/** The prescription dose this product will fill. */
export type DoseLink = LinkedDose;

interface Props {
  /** The product's unit of measure; only drugs of this form are offered. */
  scale: string;
  value: DoseLink | null;
  onChange: (link: DoseLink | null) => void;
}

const inputCls =
  "h-9 px-2.5 rounded-md border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200 text-xs outline-none focus:ring-2 focus:ring-indigo-500";
const ghostBtn =
  "px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors text-[11px] font-medium shrink-0";
const primaryBtn =
  "px-2.5 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors text-[11px] font-medium shrink-0";

const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message || fallback;

const PrescriptionLinkPicker: React.FC<Props> = ({ scale, value, onChange }) => {
  const [drugs, setDrugs] = useState<LinkableDrug[]>([]);
  const [drug, setDrug] = useState<LinkableDrug | null>(null);
  const [editing, setEditing] = useState(false);
  const [newDose, setNewDose] = useState("");
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ drugs: "", type: "mg", dose: "" });
  const [busy, setBusy] = useState(false);

  const loadDrugs = useCallback(
    (keyword: string) => {
      if (!scale) return;
      searchDrugsForProductLink(keyword, scale)
        .then(setDrugs)
        .catch((error) => console.error(error?.response));
    },
    [scale]
  );

  useEffect(() => {
    loadDrugs("");
    setDrug(null);
  }, [loadDrugs]);

  // A unit change can leave the chosen drug's form behind.
  useEffect(() => {
    if (value?.form && scale && value.form !== scale) onChange(null);
  }, [scale, value, onChange]);

  const pickDrug = (e: { target: { value: any } }) => {
    setDrug(drugs.find((d) => d._id === e.target.value) || null);
    setNewDose("");
  };

  const pickDose = (d: LinkableDrug, doseId: string, doseValue: string) => {
    onChange({
      prescriptionId: d._id,
      drugName: d.drugs,
      type: d.type,
      form: d.form || scale,
      doseId,
      doseValue,
    });
    setEditing(false);
    setDrug(null);
  };

  const saveNewDose = async () => {
    const v = newDose.trim();
    if (!drug || !v) return;
    setBusy(true);
    try {
      const dose = await addDoseToDrug(drug._id, v);
      setNewDose("");
      pickDose(drug, dose._id, dose.value);
      loadDrugs("");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to add the dose."));
    } finally {
      setBusy(false);
    }
  };

  const saveNewDrug = async () => {
    const name = draft.drugs.trim();
    const dose = draft.dose.trim();
    if (!name || !dose) {
      toast.error("Enter the drug name and a dose.");
      return;
    }
    setBusy(true);
    try {
      const created = await createDrug({
        drugs: name,
        type: draft.type,
        form: scale,
        doses: [dose],
      });
      const first = created.dose?.[0];
      if (first) {
        onChange({
          prescriptionId: created._id,
          drugName: name,
          type: draft.type,
          form: scale,
          doseId: first._id,
          doseValue: first.value,
        });
      }
      setCreating(false);
      setEditing(false);
      setDraft({ drugs: "", type: "mg", dose: "" });
      loadDrugs("");
    } catch (error) {
      toast.error(errorMessage(error, "Failed to create the drug."));
    } finally {
      setBusy(false);
    }
  };

  const unit = scaleLabel(scale);

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-600 p-2.5 space-y-2">
      <div>
        <p className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          Drug &amp; strength
        </p>
        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
          {isPerUnitScale(scale)
            ? `Which drug this is and how strong one ${unit.toLowerCase()} is, so prescriptions can bill it from stock.`
            : "Which prescribed dose this product fills, so prescriptions can bill it from stock. Quantity is entered at billing."}
        </p>
      </div>

      {!scale ? (
        <p className="text-[11px] text-slate-400 dark:text-slate-500 py-1">
          Pick the unit of measure first.
        </p>
      ) : value && !editing ? (
        <div className="flex items-center justify-between gap-2 rounded-md bg-indigo-50 dark:bg-indigo-900/40 px-2.5 py-2">
          <p className="min-w-0 truncate text-xs font-semibold text-slate-700 dark:text-slate-200">
            {linkSummary(value, scale)}
          </p>
          <div className="flex gap-1.5">
            <button type="button" className={ghostBtn} onClick={() => setEditing(true)}>
              Change
            </button>
            <button type="button" className={ghostBtn} onClick={() => onChange(null)}>
              Remove
            </button>
          </div>
        </div>
      ) : creating ? (
        <div className="space-y-2 rounded-md bg-slate-50 dark:bg-slate-700/40 p-2">
          <p className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
            New drug · Form {unit}
          </p>
          <input
            value={draft.drugs}
            onChange={(e) => setDraft((d) => ({ ...d, drugs: e.target.value }))}
            placeholder="Drug name, e.g. Amoxicillin"
            className={`${inputCls} w-full`}
          />
          <div className="flex gap-1.5">
            <input
              value={draft.dose}
              onChange={(e) => setDraft((d) => ({ ...d, dose: e.target.value }))}
              placeholder={
                isPerUnitScale(scale)
                  ? `Strength of 1 ${unit.toLowerCase()}, e.g. 250`
                  : "Dose, e.g. 5"
              }
              className={`${inputCls} flex-1 min-w-0`}
            />
            <select
              value={draft.type}
              onChange={(e) => setDraft((d) => ({ ...d, type: e.target.value }))}
              className={`${inputCls} w-20`}
              aria-label="Strength unit"
            >
              {strengthUnits.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-1.5">
            <button type="button" className={ghostBtn} onClick={() => setCreating(false)}>
              Cancel
            </button>
            <button type="button" className={primaryBtn} disabled={busy} onClick={saveNewDrug}>
              Create drug
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-end gap-1.5">
            <div className="flex-1 min-w-0">
              <InputDropSearch
                data={drugs}
                name="linkDrug"
                label="Drug"
                selectName1="drugs"
                selectValue="_id"
                value={drug?._id || ""}
                onChange={pickDrug}
                onSearch={loadDrugs}
              />
            </div>
            <button
              type="button"
              className={`${ghostBtn} h-10`}
              onClick={() => setCreating(true)}
            >
              + New drug
            </button>
          </div>
          <p className="text-[10px] text-slate-400 dark:text-slate-500">
            Only {unit} drugs are listed, plus drugs with no form yet.
          </p>

          {drug && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-medium text-slate-600 dark:text-slate-300">
                {doseFieldLabel(scale)}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {drug.dose.map((d) => {
                  const selected = value?.doseId === d._id;
                  const others = d.products.map((p) => p.productName).join(", ");
                  return (
                    <button
                      key={d._id}
                      type="button"
                      disabled={d.blocked}
                      onClick={() => pickDose(drug, d._id, d.value)}
                      title={
                        d.blocked
                          ? `Filled by ${others} in another unit`
                          : others
                          ? `Also filled by ${others}`
                          : undefined
                      }
                      className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-medium transition-colors ${
                        selected
                          ? "bg-indigo-600 border-indigo-600 text-white"
                          : d.blocked
                          ? "border-slate-200 dark:border-slate-600 text-slate-300 dark:text-slate-500 line-through cursor-not-allowed"
                          : "border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
                      }`}
                    >
                      {d.value}
                      {drug.type}
                      {d.products.length > 0 && !d.blocked && (
                        <span className="ml-1 opacity-60">· {d.products.length}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="flex gap-1.5">
                <input
                  value={newDose}
                  onChange={(e) => setNewDose(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      saveNewDose();
                    }
                  }}
                  placeholder={`New dose, e.g. 875 (${drug.type || "unit"})`}
                  className={`${inputCls} flex-1 min-w-0`}
                />
                <button
                  type="button"
                  className={primaryBtn}
                  disabled={busy || !newDose.trim()}
                  onClick={saveNewDose}
                >
                  + Add dose
                </button>
              </div>
            </div>
          )}

          {editing && (
            <button type="button" className={ghostBtn} onClick={() => setEditing(false)}>
              Keep {value?.drugName} {value?.doseValue}
              {value?.type}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(PrescriptionLinkPicker);
